<script lang="ts">
	import type { Customer, CustomerType } from 'utility/customer-helper'
	import type { i18n } from 'types/common'

	import { hasPermission } from 'utility/permission'
	import { getEventValue } from '@isoftdata/browser-event'
	import { stringToBoolean } from '@isoftdata/utility-string'

	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import BooleanIndicator from 'components/BooleanIndicator.svelte'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import CurrencyInput from '@isoftdata/svelte-currency-input'
	import Input from '@isoftdata/svelte-input'
	import Table, { Td } from '@isoftdata/svelte-table'
	import Textarea from '@isoftdata/svelte-textarea'
	import { getContext } from 'svelte'

	interface Props {
		customer: Customer
		customerTypeList?: Array<CustomerType>
		customerChanged: boolean
		viewOnlyMode?: boolean
	}

	let {
		customer = $bindable(),
		customerTypeList = $bindable([]),
		customerChanged = $bindable(),
		viewOnlyMode = false,
	}: Props = $props()

	let customerCustomFieldsValue = $state(
		customer.optionValues.map(optionValue => {
			return {
				...optionValue,
				id: optionValue.option.id,
			}
		}),
	)

	const { t: translate } = getContext<i18n>('i18next')

	let customerCanEditCustomFields = hasPermission('CUSTOMERS_CAN_MANAGE_OPTIONS', customer.store?.id)

	function optionValueChanged(event: Event, rowIndex: number) {
		const value = getEventValue(event)
		customer.optionValues[rowIndex].value = value
		customerCustomFieldsValue[rowIndex].value = value
		customerChanged = true
	}
</script>

<div class="row">
	<div class="col-12 mb-2">
		<Checkbox
			inline
			type="radio"
			label={translate('common:status', 'Status')}
			trueLabel={translate('common:active', 'Active')}
			falseLabel={translate('common:inactive', 'Inactive')}
			disabled={viewOnlyMode}
			bind:checked={customer.active}
			onchange={() => (customerChanged = true)}
		/>
	</div>
	<div class="col-12 col-md-4">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">{translate('common:additionalInfo', 'Additional Info')}</h5>
			</div>
			<div class="card-body">
				<Autocomplete
					canAddNew
					label={translate('customer.customerType', 'Customer Type')}
					options={customerTypeList}
					disabled={viewOnlyMode}
					emptyValue={''}
					bind:value={customer.type}
					change={() => (customerChanged = true)}
				/>
				<Input
					label={translate('customer.driverLicenseNumber', 'Driver License Number')}
					disabled={viewOnlyMode}
					bind:value={customer.driverLicenseNumber}
					onchange={() => (customerChanged = true)}
				/>
				<Input
					label={translate('customer.webPage', 'Web Page')}
					disabled={viewOnlyMode}
					bind:value={customer.webAddress}
					onchange={() => (customerChanged = true)}
				>
					{#snippet append()}
						<Button
							target="_blank"
							iconClass="external-link-alt"
							href={customer.webAddress}
							disabled={!customer.webAddress || viewOnlyMode}
						/>
					{/snippet}
				</Input>
				<Input
					readonly
					type="date"
					label={translate('customer.dateEntered', 'Date Entered')}
					value={customer.dateEntered?.toISOString().split('T')[0] ?? new Date().toISOString().split('T')[0]}
				/>
				<Textarea
					label={translate('common:notes', 'Notes')}
					disabled={viewOnlyMode}
					bind:value={customer.notes}
					onchange={() => (customerChanged = true)}
				/>
			</div>
		</div>
	</div>
	<div class="col-12 col-md-8">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">{translate('customer.customerCustomFields', 'Customer Custom Fields')}</h5>
			</div>
			<div class="card-body">
				<Table
					responsive
					idProp="id"
					parentStyle="overflow-y: auto; max-height: 55vh;"
					columns={[
						{ property: 'option[label]', name: translate('common:option', 'Option') },
						{ property: 'value', name: translate('common:value', 'Value') },
						{
							property: 'option[showInCustomerList]',
							name: translate('common:showInSearch', 'Show In Search'),
							align: 'center',
							width: '1rem',
						},
					]}
					rows={customerCustomFieldsValue}
				>
					{#snippet children({ row })}
						<tr>
							<Td property="option[label]">
								{row.option.label}
							</Td>
							<Td property="value">
								{#if row.option.dataType === 'BOOLEAN'}
									<Checkbox
										showLabel={false}
										type="radio"
										checked={stringToBoolean(row.value)}
										disabled={viewOnlyMode || !customerCanEditCustomFields}
										onchange={event => optionValueChanged(event, row.originalIndex)}
									/>
								{:else if row.option.dataType === 'DATE'}
									<Input
										showLabel={false}
										type="date"
										value={row.value}
										disabled={viewOnlyMode || !customerCanEditCustomFields}
										onchange={event => optionValueChanged(event, row.originalIndex)}
									/>
								{:else if row.option.dataType === 'NUMBER'}
									<Input
										showLabel={false}
										type="number"
										value={row.value}
										disabled={viewOnlyMode || !customerCanEditCustomFields}
										onchange={event => optionValueChanged(event, row.originalIndex)}
									/>
								{:else if row.option.dataType === 'CURRENCY'}
									<CurrencyInput
										showLabel={false}
										value={row.value}
										disabled={viewOnlyMode || !customerCanEditCustomFields}
										change={ctx => {
											const value = ctx.value
											customer.optionValues[row.originalIndex].value = value
											customerChanged = true
										}}
									/>
								{:else}
									<Input
										showLabel={false}
										value={row.value}
										disabled={viewOnlyMode || !customerCanEditCustomFields}
										onchange={event => optionValueChanged(event, row.originalIndex)}
									/>
								{/if}
							</Td>
							<Td property="option[showInCustomerList]">
								<BooleanIndicator value={row.option.showInCustomerList} />
							</Td>
						</tr>
					{/snippet}
				</Table>
			</div>
		</div>
	</div>
</div>
